import { useState, useEffect } from "react";

import { Fingerprint, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { getCurrentDeviceName } from "@/hooks/useDeviceFingerprint";
import { authClient } from "@/lib/auth-client";

interface PasskeySetupProps {
  onComplete?: () => void;
}

export const PasskeySetup = ({ onComplete }: PasskeySetupProps) => {
  const [loading, setLoading] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [deviceName, setDeviceName] = useState("");

  useEffect(() => {
    const checkSupport = async () => {
      const supported = await authClient.passkey.isSupported();
      setIsSupported(supported);
    };
    checkSupport();
    setDeviceName(getCurrentDeviceName());
  }, []);

  const handleRegister = async () => {
    if (!deviceName.trim()) {
      toast.error("Please enter a device name");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await authClient.passkey.addPasskey({
        name: deviceName,
      });

      if (error) {
        throw error;
      }

      toast.success("Passkey registered successfully!", {
        description: "You can now use this device for passwordless authentication",
      });

      onComplete?.();
    } catch (error: any) {
      console.error("Passkey registration failed:", error);
      toast.error(error.message || "Failed to register passkey");
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Your browser doesn't support passkeys. Please use a modern browser like Chrome, Safari, or Edge.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Alert className="border-primary/50 bg-primary/5">
        <CheckCircle2 className="h-4 w-4 text-primary" />
        <AlertDescription>
          <strong>Benefits:</strong> No passwords to remember, faster login, more secure against phishing
        </AlertDescription>
      </Alert>

      <div className="space-y-2">
        <Label htmlFor="device-name">Device Name</Label>
        <Input
          id="device-name"
          value={deviceName}
          onChange={(e) => setDeviceName(e.target.value)}
          placeholder="My iPhone"
          maxLength={200}
        />
        <p className="text-xs text-muted-foreground">
          This helps you identify this device later
        </p>
      </div>

      <Button
        type="button"
        className="w-full"
        onClick={handleRegister}
        disabled={!deviceName.trim() || loading}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Registering...
          </>
        ) : (
          <>
            <Fingerprint className="mr-2 h-4 w-4" />
            Register Passkey
          </>
        )}
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        You'll be prompted to use your device's biometric authentication
      </p>
    </div>
  );
};
